import { formatNumber } from '@angular/common';
import { Component, Inject, Injector, LOCALE_ID, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { AbstractComponent, CacheKeyEnum, Campaign, CampaignFailureSummary, CampaignMessageService, CampaignService, CampaignStatusEnum, Channel, ChannelService, ChannelTypeEnum, channelIconOf, ConfirmationComponent, ConfirmationLegacyComponent, ListState, Pager, SearchParams, } from 'lib-trend-core';
import { Observable, Subject, catchError, debounceTime, distinctUntilChanged, finalize, forkJoin, of, switchMap, takeUntil, tap, } from 'rxjs';
import { CampaignRefreshPreferenceService } from '../shared/campaign-refresh-preference.service';

/** Uma linha do `$group` de `GET /api/campaign/fetch/analytics`. */
interface CampaignAnalytics {
  coust: number;
  totalSent: number;
  totalMessages: number;
  totalFailed: number;
  totalRecord: number;
}

interface CampaignKpiSnapshot {
  totalCampaign: string;
  totalSent: string;
  totalDelivered: string;
  totalFailed: string;
  totalCoust: number;
  deliveryRate: number | null;
  deliveryRateDelta: number | null;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

@Component({
    selector: 'app-campaign-list',
    templateUrl: './campaign-list.v2.component.html',
    // templateUrl: './campaign-list.component.html',
    styleUrl: './campaign-list.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CampaignListComponent extends AbstractComponent implements OnInit {

  campaignStatuses = [
    { status: null, label: 'campaign.campaignList.statusAll' },
    { status: CampaignStatusEnum.WAITING, label: 'campaign.campaignList.statusWaiting' },
    { status: CampaignStatusEnum.SENDING, label: 'campaign.campaignList.statusSending' },
    { status: CampaignStatusEnum.DONE, label: 'campaign.campaignList.statusDone' },
    { status: CampaignStatusEnum.CANCELED, label: 'campaign.campaignList.statusCanceled' },
    { status: CampaignStatusEnum.ERROR, label: 'campaign.campaignList.statusError' },
  ];

  filterStatus: string = '';
  labelFilterChannel: string = 'campaign.campaignList.allChannels';
  channels: Array<Channel> = [];

  pager: Pager<Campaign> = new Pager<Campaign>({ perPage: 10 });
  listObservable: Observable<Pager<Campaign>>;

  totalCampaign: string = '0';
  totalSent: string = '0';
  totalDelivered: string = '0';
  totalCoust: number = 0;
  totalFailed: string = '0';
  deliveryRate: number | null = null;
  deliveryRateDelta: number | null = null;


  private static readonly DELTA_DISPLAY_THRESHOLD = 0.05;
  private static readonly DEFAULT_PERIOD_DAYS = 30;

  @ViewChild('picker', { static: true }) picker: MatDatepicker<Date>;
  filterForm: FormGroup;

  private termOfSearch: Subject<string> = new Subject<string>();
  private selectedChannelId: string | null = null;

  constructor(
    public injector: Injector,
    public dialog: MatDialog,
    public campaignService: CampaignService,
    public channelService: ChannelService,
    private refreshPreference: CampaignRefreshPreferenceService,
    private campaignMessageService: CampaignMessageService,
    /* O idioma é resolvido em runtime por `LanguageService`, então formatar
       número fora do template exige o locale corrente — não um fixo. */
    @Inject(LOCALE_ID) private readonly locale: string,
  ) {
    super(injector);
    this.loadingContent = true;
    this.cacheKey = CacheKeyEnum.CAMPAIGN_LIST;
  }

  /***/
  ngOnInit() {
    const cached = this.restoreState<Campaign>();

    this.createPeriodForm(cached?.params);
    this.setupObservableSearch();
    this.loadAllChannels();
    this.configPolling();

    if (cached) {
      this.applyCachedState(cached);
      return;
    }

    this.getList();
  }

  /* ────────────────────────────────────────────────────────────────────
     Periodic refresh (DEV-2034 — polling instead of push)
     ──────────────────────────────────────────────────────────────────── */

  /**
   * Silently reloads the screen at the cadence chosen in the selector — the
   * same shared `ticks$` the dispatch panel consumes, which already pauses
   * while the tab is hidden and refreshes immediately on return.
   */
  private configPolling(): void {
    this.refreshPreference.ticks$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.reloadQuietly());
  }

  private quietReloadInFlight = false;

  private reloadQuietly(): void {
    // Skip the tick while the previous one is still in flight: stacking
    // duplicate reads against a slow backend only makes it slower.
    if (this.quietReloadInFlight) {
      return;
    }
    this.quietReloadInFlight = true;

    this.getDataAnalytics(true);

    const requestedPage = this.pager.page;
    const requestedSearch = this.searchString;
    const requestedParams = JSON.stringify(this.searchParams);

    this.campaignService
      .getAll(this.pager.page, this.pager.perPage, this.searchString, this.searchParams)
      .pipe(finalize(() => (this.quietReloadInFlight = false)), takeUntil(this.destroy$))
      .subscribe({
        next: (pager: Pager<Campaign>) => {
          // A late tick response must never clobber a page/filter/search the
          // operator changed while it was in flight — nor be persisted into
          // the list cache, where it would survive navigation.
          if (
            requestedPage !== this.pager.page ||
            requestedSearch !== this.searchString ||
            requestedParams !== JSON.stringify(this.searchParams)
          ) {
            return;
          }
          this.pager = pager;
          this.loadFailures();
          this.persistState();
        },

        error: () => { },
      });
  }

  /* ────────────────────────────────────────────────────────────────────
     Concluído com erros
     ──────────────────────────────────────────────────────────────────── */

  /**
   * Failures per dispatch, for the rows on screen.
   *
   * The list row carries the `Campaign`, which knows nothing about
   * `CampaignMessage` — hence the map. It comes from the per-page query, made
   * on load and repeated on every polling tick (DEV-2034).
   *
   * A missing key means "no failure", never "unknown": the backend only
   * returns dispatches with `failed > 0`.
   */
  private failuresById = new Map<string, CampaignFailureSummary>();

  /**
   * O resumo de falhas de uma linha, ou `null` quando não houve nenhuma.
   *
   * Devolver `null` faz o `@if` do template servir de teste e de fonte ao mesmo
   * tempo, sem um segundo método só para perguntar "tem erro?".
   */
  failureOf(campaign: Campaign): CampaignFailureSummary | null {
    return this.failuresById.get(String(campaign?._id)) ?? null;
  }

  /**
   * Busca as falhas dos disparos desta página — uma chamada, não uma por linha.
   *
   * Silenciosa no erro de propósito: é um enfeite do rótulo de status, e a
   * lista tem de continuar utilizável sem ela. Sem falha conhecida, a linha
   * mostra "Concluído", que é o que ela mostrava antes desta feature.
   */
  private loadFailures(): void {
    const ids = (this.pager?.list ?? []).map((campaign) => String(campaign._id)).filter(Boolean);
    if (!ids.length) {
      this.failuresById.clear();
      return;
    }

    this.campaignMessageService.getCampaignFailures(ids)
      .pipe(catchError(() => of<Array<CampaignFailureSummary>>([])), takeUntil(this.destroy$))
      .subscribe((failures) => {
        // Recriado, não remendado: as linhas mudaram, e uma entrada de uma
        // página anterior descreveria um disparo que não está mais na tela.
        this.failuresById = new Map(
          (failures ?? []).map((failure) => [String(failure.campaign), failure]),
        );
      });
  }

  private applyCachedState(cached: ListState<Campaign>): void {
    this.pager = cached.pager;
    // O cache guarda as linhas, não as falhas: sem isto, voltar para a lista
    // pintaria "Concluído" numa linha que concluiu com erro.
    this.loadFailures();
    this.searchString = cached.search ?? '';
    this.searchParams = cached.params ?? {};

    this.filterStatus = String(this.searchParams['status'] ?? '');
    this.selectedChannelId = (this.searchParams['channel'] as string) ?? null;

    const kpis = (cached.extraData?.['kpis'] ?? {}) as Partial<CampaignKpiSnapshot>;
    this.totalCampaign = kpis.totalCampaign ?? '0';
    this.totalSent = kpis.totalSent ?? '0';
    this.totalDelivered = kpis.totalDelivered ?? '0';
    this.totalFailed = kpis.totalFailed ?? '0';
    this.totalCoust = kpis.totalCoust ?? 0;
    this.deliveryRate = kpis.deliveryRate ?? null;
    this.deliveryRateDelta = kpis.deliveryRateDelta ?? null;

    this.loadingContent = false;
  }

  private persistState(): void {
    this.saveState<Campaign>({
      pager: this.pager,
      search: this.searchString,
      params: this.searchParams,
      extraData: {
        kpis: <CampaignKpiSnapshot>{
          totalCampaign: this.totalCampaign,
          totalSent: this.totalSent,
          totalDelivered: this.totalDelivered,
          totalFailed: this.totalFailed,
          totalCoust: this.totalCoust,
          deliveryRate: this.deliveryRate,
          deliveryRateDelta: this.deliveryRateDelta,
        },
      },
    });
  }

  private createPeriodForm(cachedParams?: Partial<SearchParams>): void {
    const fallback = this.periodOfDays(CampaignListComponent.DEFAULT_PERIOD_DAYS);

    const dateStart = cachedParams?.['dateStart']
      ? this.fromCalendarDay(String(cachedParams['dateStart']))
      : fallback.dateStart;
    const dateEnd = cachedParams?.['dateEnd']
      ? this.fromCalendarDay(String(cachedParams['dateEnd']))
      : fallback.dateEnd;

    this.filterForm = this.formBuilder.group({
      dateRange: this.formBuilder.group({
        dateStart: [dateStart],
        dateEnd: [dateEnd],
      }),
    });

    this.filterForm.get('dateRange').valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(300),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      )
      .subscribe(({ dateStart, dateEnd }) => {
        if (dateStart && dateEnd) {
          this.pager.page = 1;
          this.getList();
        }
      });
  }

  private toCalendarDay(value: Date): string {
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    return `${value.getFullYear()}-${month}-${day}`;
  }

  private fromCalendarDay(value: string): Date {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  private periodOfDays(days: number): { dateStart: Date; dateEnd: Date } {
    const dateEnd = new Date();
    const dateStart = new Date();

    if (days > 1) {
      dateStart.setDate(dateStart.getDate() - (days - 1));
    }

    return { dateStart, dateEnd };
  }

  onPeriodChange(event: MatSelectChange): void {
    this.filterForm.get('dateRange').patchValue(this.periodOfDays(Number(event.value)));
    this.picker.close();
  }

  getList(channelId: string | null = this.selectedChannelId, refreshAnalytics: boolean = true) {
    this.loadingContent = true;
    this.searchParams = {
      company: this.getIDCurrentCompany(),
    };
    if (channelId) {
      this.searchParams['channel'] = channelId;
    }

    if (this.filterStatus) {
      this.searchParams['status'] = this.filterStatus;
    }

    const { dateStart, dateEnd } = this.filterForm.get('dateRange').value;
    if (dateStart && dateEnd) {
      this.searchParams['dateStart'] = this.toCalendarDay(dateStart);
      this.searchParams['dateEnd'] = this.toCalendarDay(dateEnd);
    }

    if (refreshAnalytics) {
      this.getDataAnalytics();
    }

    this.campaignService
      .getAll(
        this.pager.page,
        this.pager.perPage,
        this.searchString,
        this.searchParams
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (pager: Pager<Campaign>) => {
          this.pager = pager;
          this.loadFailures();
          this.persistState();
        },
        complete: () => (this.loadingContent = false),
        error: (err) => this.alertService.error(err.error.message),
      });
  }

  /***/
  loadPage(page: number) {
    this.pager.page = page;
    this.getList(this.selectedChannelId, false);
  }

  edit(campaign: Campaign) {
    this.router.navigate([`/campaign/${campaign._id}/edit`], {
      relativeTo: this.route,
    })
  }

  /***/
  delete(campaign: Campaign) {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '600px',
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (Boolean(result) === true) {
        this.campaignService.delete(campaign._id).subscribe({
          next: (value) => {
            this.getList();
            this.alertService.success();
          },
          error: (err) => this.alertService.error(err.error.message),
        })
      }
    });
  }

  /***/
  showNewCampaign() {
    this.router.navigate(['/campaign/new'], { relativeTo: this.route });
  }

  /***/
  setupObservableSearch() {
    this.listObservable = this.termOfSearch
      .pipe(debounceTime(500))
      .pipe(distinctUntilChanged())
      .pipe(tap(() => this.getDataAnalytics()))
      .pipe(
        switchMap((term) => {
          return this.campaignService.getAll(
            this.pager.page,
            this.pager.perPage,
            term,
            this.searchParams
          );
        })
      )
    this.listObservable
      .pipe(takeUntil(this.destroy$))
      .subscribe((pager: Pager<Campaign>) => {
        this.pager = pager;
        this.loadFailures();
        this.persistState();
      });
  }

  isStatusActive(status: CampaignStatusEnum | null): boolean {
    return status === null ? !this.filterStatus : this.filterStatus === status;
  }

  onChannelChange(channelId: string): void {
    this.selectedChannelId = channelId || null;
    this.labelFilterChannel = this.resolveChannelLabel(this.selectedChannelId);
    this.pager.page = 1;
    this.getList(this.selectedChannelId);
  }

  /***/
  search(term: string) {
    this.pager.page = 1;
    this.searchString = term;
    this.termOfSearch.next(term);
  }

  formatTags(campaign: Campaign) {
    return campaign.audienceTags?.map((t) => t.title).join(', ');
  }

  formatContactGroups(campaign: Campaign) {
    return campaign.audienceContactGroup?.map(t => t.name).join(', ');
  }

  // The previous-period window barely ever changes between two silent ticks;
  // re-running its aggregation on every poll would double the analytics cost
  // for a byte-identical answer. User-initiated loads always refresh it.
  private previousAnalyticsCache: { key: string; rows: CampaignAnalytics[] } | null = null;

  private getDataAnalytics(silent = false): void {
    const params: Partial<SearchParams> = { ...this.searchParams };
    if (this.searchString) {
      params['search'] = this.searchString;
    }

    const previousParams = this.buildPreviousPeriodParams(params);
    const previousKey = JSON.stringify(previousParams);
    const cachedPrevious = silent && this.previousAnalyticsCache?.key === previousKey
      ? this.previousAnalyticsCache.rows
      : null;

    forkJoin({
      current: this.fetchAnalytics(params),
      // A filter without a date range has no defined previous window to compare.
      previous: cachedPrevious
        ? of(cachedPrevious)
        : previousParams ? this.fetchAnalytics(previousParams) : of<CampaignAnalytics[]>([]),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ current, previous }) => {
          this.previousAnalyticsCache = { key: previousKey, rows: previous };
          this.applyAnalytics(current, previous, silent);
        },
        error: () => {
          if (silent) {
            return;
          }
          this.alertService.error(
            this.t('campaign.campaignList.errorLoadAnalytics')
          );
        },
      });
  }

  private fetchAnalytics(params: Partial<SearchParams>): Observable<CampaignAnalytics[]> {
    return this.campaignService.getDataAnalytics(params) as Observable<CampaignAnalytics[]>;
  }

  private applyAnalytics(current: CampaignAnalytics[], previous: CampaignAnalytics[], silent = false): void {
    const delivered = this.sumBy(current, 'totalMessages');
    const sent = this.sumBy(current, 'totalSent');

    this.totalCampaign = this.sumBy(current, 'totalRecord').toString();
    this.totalSent = sent.toString();
    this.totalDelivered = delivered.toString();
    this.totalFailed = this.sumBy(current, 'totalFailed').toString();
    this.totalCoust = this.sumBy(current, 'coust');

    this.deliveryRate = this.deliveryRateOf(delivered, sent);

    const previousRate = this.deliveryRateOf(
      this.sumBy(previous, 'totalMessages'),
      this.sumBy(previous, 'totalSent'),
    );

    this.deliveryRateDelta =
      this.deliveryRate !== null && previousRate !== null
        ? this.deliveryRate - previousRate
        : null;

    // On the silent (polling) path the pager subscription already persists the
    // whole state; persisting here again would serialize the full pager twice
    // per tick.
    if (!silent) {
      this.persistState();
    }
  }

  private sumBy(rows: CampaignAnalytics[], field: keyof CampaignAnalytics): number {
    return rows.reduce((acc, row) => acc + (row[field] || 0), 0);
  }

  /**
   * Entregues sobre enviadas — a mesma conta do painel do disparo, de
   * propósito: as duas telas descrevem os mesmos disparos e não podem
   * divergir. Sem nenhuma aceita não há denominador, e aí é `null` ("—").
   *
   * O teto de 100% cobre o recibo que chega sem aceite registrado (no RCS o
   * aviso de leitura não marca entrega, e um aceite recusado pode receber
   * recibo depois), que produziria um percentual impossível.
   */
  private deliveryRateOf(delivered: number, sent: number): number | null {
    return sent > 0 ? Math.min(100, (delivered / sent) * 100) : null;
  }

  private buildPreviousPeriodParams(params: Partial<SearchParams>): Partial<SearchParams> | null {
    const dateStart = params['dateStart'];
    const dateEnd = params['dateEnd'];
    if (typeof dateStart !== 'string' || typeof dateEnd !== 'string') {
      return null;
    }

    const start = this.fromCalendarDay(dateStart);
    const end = this.fromCalendarDay(dateEnd);

    const days = Math.round((end.getTime() - start.getTime()) / MS_PER_DAY) + 1;

    const previousEnd = new Date(start);
    previousEnd.setDate(previousEnd.getDate() - 1);

    const previousStart = new Date(previousEnd);
    previousStart.setDate(previousStart.getDate() - (days - 1));

    return {
      ...params,
      dateStart: this.toCalendarDay(previousStart),
      dateEnd: this.toCalendarDay(previousEnd),
    };
  }

  get deliveryTrend(): 'up' | 'down' | 'stable' | null {
    if (this.deliveryRateDelta === null) {
      return null;
    }
    if (Math.abs(this.deliveryRateDelta) < CampaignListComponent.DELTA_DISPLAY_THRESHOLD) {
      return 'stable';
    }
    return this.deliveryRateDelta > 0 ? 'up' : 'down';
  }

  get deliveryRateDeltaAbs(): number {
    return Math.abs(this.deliveryRateDelta ?? 0);
  }

  get hasFailures(): boolean {
    return Number(this.totalFailed) > 0;
  }

  get deliveryTrendTone(): string | null {
    if (this.deliveryRate === null) {
      return null;
    }
    switch (this.deliveryTrend) {
      case 'up':
        return 'success';
      case 'down':
        return 'danger';
      default:
        return null;
    }
  }

  get deliveryRateLabel(): string {
    if (this.deliveryRate === null) {
      return '—';
    }
    return `${formatNumber(this.deliveryRate, this.locale, '1.1-1')}%`;
  }

  get deliveryTrendLabel(): string {
    if (this.deliveryRate === null) {
      return this.t('campaign.campaignList.kpiDeliveryNoData');
    }

    const delta = formatNumber(this.deliveryRateDeltaAbs, this.locale, '1.1-1');

    switch (this.deliveryTrend) {
      case 'up':
        return `▲ ${this.t('campaign.campaignList.kpiDeliveryVsPrevious', { delta })}`;
      case 'down':
        return `▼ ${this.t('campaign.campaignList.kpiDeliveryVsPrevious', { delta })}`;
      case 'stable':
        return this.t('campaign.campaignList.kpiDeliveryStable');
      default:
        return this.t('campaign.campaignList.kpiDeliveryNoBaseline');
    }
  }

  listCampaignContact(item: Campaign) {
    if (item.enabled === true) {
      this.router.navigate([`/campaign/${item._id}/messages`], {
        relativeTo: this.route,
        state: {
          audienceTags: item.audienceTags
        }
      });
    } else {
      this.router.navigate([`/campaign/${item._id}/edit`], {
        relativeTo: this.route,
        state: {
          audienceTags: item.audienceTags
        }
      });
    }
  }

  private loadAllChannels() {
    this.channelService.getList().pipe(takeUntil(this.destroy$)).subscribe(channels => {
      if (channels) {
        this.channels = channels.filter((channel) => channel.type !== ChannelTypeEnum.INSTAGRAM_API);
        this.labelFilterChannel = this.resolveChannelLabel(this.selectedChannelId);
      }
    })
  }

  private resolveChannelLabel(channelId: string | null): string {
    return this.channels.find((channel) => channel._id === channelId)?.name
      ?? 'campaign.campaignList.allChannels';
  }

  getChannelIcon(channel: Channel): string { return channelIconOf(channel); }

  setStatusFilter(status: CampaignStatusEnum | null) {
    this.filterStatus = status ?? '';
    this.pager.page = 1;
    this.getList(this.selectedChannelId);
  }

  cancel(campaign: Campaign): void {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '500px',
      data: this.t('campaign.campaignList.confirmCancelSend'),
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (Boolean(result) === true) {
        this.campaignService.updateByStatus(campaign._id, CampaignStatusEnum.CANCELED).subscribe({
          next: () => {
            this.getList();
            this.alertService.success();
          },
          error: (err) => this.alertService.error(err.error.message)
        })
      }
    })
  }

  truncateText(text: string | undefined, maxLength: number): string {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  }

  getTruncatedTags(campaign: Campaign, maxTags: number): string {
    if (!campaign.audienceTags || campaign.audienceTags.length === 0) return '';
    const tags = campaign.audienceTags.map(t => t.title);
    return tags.slice(0, maxTags).join(', ');
  }

  getTruncatedContactGroups(campaign: Campaign, maxGroups: number): string {
    if (!campaign.audienceContactGroup || campaign.audienceContactGroup.length === 0) return '';
    const groups = campaign.audienceContactGroup.map(t => t.name);
    return groups.slice(0, maxGroups).join(', ');
  }

  formatTemplateParams(params?: string[]): string {
    if (!params || params.length === 0) return '';
    return params.join(', ');
  }

  getShortTemplateParams(params?: string[]): string {
    if (!params || params.length === 0) return '';

    const joinedParams = params.join(', ');
    return this.truncateText(joinedParams, 20);
  }

  cloneCampaign(campaign: Campaign): void {
    const dialogRef = this.dialog.open(ConfirmationLegacyComponent, {
      width: '600px',
    });
    dialogRef.afterClosed().subscribe(result => {
      if (Boolean(result) === true) {
        this.campaignService.cloneCampaign(campaign._id).subscribe({
          next: (newCampaign: Campaign) => {
            this.alertService.success(this.t('campaign.campaignList.successCloned'));
            this.router.navigate([`/campaign/${newCampaign._id}/edit`], { relativeTo: this.route });
          },
          error: (err) => this.alertService.error(err.error.message)
        });
      }
    });
  }

}
